import { supabase } from './supabase'
import type { Database } from '@/types/database-extensions'
import { handleApiError } from '@/utils/error-handlers'
import { AttendanceStatus } from '@/types/event-types'

/**
 * Type representing an event participant from the database
 */
export type EventParticipant = Database['public']['Tables']['event_participants']['Row']

/**
 * Interface for the event participant with nested profile data as returned by the API
 */
interface ParticipantWithProfile {
  id: string
  created_at: string
  event_id: string
  profile_id: string
  status: string
  is_waitlisted: boolean
  waitlist_position: number | null
  rsvp_notes?: string | null
  profiles: {
    id: string
    username: string | null
    full_name: string | null
    avatar_url: string | null
  }
}

/**
 * Check if event signup is allowed based on meetup signup_start_time
 * @param eventId - The ID of the event to check
 * @returns Object containing isAllowed and optionally signupStartTime or an error
 */
export async function isEventSignupAllowed(eventId: string): Promise<{
  isAllowed?: boolean
  signupStartTime?: string
  error: string | null
}> {
  try {
    // First check if the event is part of a meetup
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select('meetup_id')
      .eq('id', eventId)
      .single()

    if (eventError) throw new Error(`Failed to get event: ${eventError.message}`)

    // If not part of a meetup, signup is always allowed
    if (!event.meetup_id) {
      return { isAllowed: true, error: null }
    }

    // Check if the meetup has a signup_start_time
    const { data: meetup, error: meetupError } = await supabase
      .from('meetups')
      .select('signup_start_time')
      .eq('id', event.meetup_id)
      .single()

    if (meetupError) throw new Error(`Failed to get meetup: ${meetupError.message}`)

    // If no signup_start_time is set, signup is always allowed
    if (!meetup.signup_start_time) {
      return { isAllowed: true, error: null }
    }

    // Compare the current time with signup_start_time
    const now = new Date()
    const signupStartTime = new Date(meetup.signup_start_time)

    const isAllowed = now >= signupStartTime

    return {
      isAllowed,
      signupStartTime: meetup.signup_start_time,
      error: null,
    }
  } catch (error) {
    return handleApiError(error, 'checking if event signup is allowed')
  }
}

/**
 * Joins an event with a specific attendance status
 * @param eventId - The ID of the event to join
 * @param profileId - The profile ID of the user joining
 * @param status - The attendance status (im_in, maybe, not_me)
 * @param notes - Optional RSVP notes
 * @returns Object containing the participant data or an error
 */
export async function joinEvent(
  eventId: string,
  profileId: string,
  status: AttendanceStatus = 'im_in',
  notes?: string
) {
  try {
    console.log(
      `[joinEvent] Starting with eventId: ${eventId}, profileId: ${profileId}, status: ${status}`
    )

    // First check if signup is allowed
    const signupCheck = await isEventSignupAllowed(eventId)

    if (signupCheck.error) throw new Error(signupCheck.error)

    if (signupCheck.isAllowed === false) {
      throw new Error(
        `Event signup is not allowed yet. Signup starts at ${signupCheck.signupStartTime}`
      )
    }

    // Check if event exists and get maxPlayers
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select('max_players')
      .eq('id', eventId)
      .single()

    if (eventError) throw new Error(`Failed to get event: ${eventError.message}`)
    console.log(`[joinEvent] Event max_players: ${event.max_players}`)

    // Get current count of confirmed attendees
    const { count: confirmedCount, error: countError } = await supabase
      .from('event_participants')
      .select('id', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .eq('status', 'im_in')
      .eq('is_waitlisted', false)

    if (countError) throw new Error(`Failed to count attendees: ${countError.message}`)
    console.log(`[joinEvent] Confirmed attendees count: ${confirmedCount}`)

    // Determine if the event is at capacity
    const isAtCapacity =
      event.max_players !== null && confirmedCount !== null && confirmedCount >= event.max_players
    console.log(`[joinEvent] Is event at capacity: ${isAtCapacity}`)

    // If user selected "I'm in" but event is at capacity, add to waitlist
    const isWaitlisted = status === 'im_in' && isAtCapacity
    const waitlistPosition = isWaitlisted ? await getNextWaitlistPosition(eventId) : null
    console.log(`[joinEvent] Will be waitlisted: ${isWaitlisted}, position: ${waitlistPosition}`)

    // Check if the user is already a participant
    const { data: existingParticipant, error: participantError } = await supabase
      .from('event_participants')
      .select('id, status, is_waitlisted')
      .eq('event_id', eventId)
      .eq('profile_id', profileId)
      .maybeSingle()

    if (participantError)
      throw new Error(`Failed to check participant: ${participantError.message}`)

    console.log(`[joinEvent] Existing participant: ${JSON.stringify(existingParticipant)}`)

    // Either update or create the participant record
    let result
    if (existingParticipant) {
      console.log(`[joinEvent] Updating existing participant with id: ${existingParticipant.id}`)
      result = await updateParticipant(
        existingParticipant.id,
        status,
        isWaitlisted,
        waitlistPosition,
        notes
      )
    } else {
      console.log(`[joinEvent] Creating new participant`)
      result = await createParticipant(
        eventId,
        profileId,
        status,
        isWaitlisted,
        waitlistPosition,
        notes
      )
    }

    console.log(`[joinEvent] Operation result: ${JSON.stringify(result)}`)
    return result
  } catch (error) {
    console.error(`[joinEvent] Error: ${error}`)
    return handleApiError(error, 'joining the event')
  }
}

/**
 * Gets the next available waitlist position for an event
 * @param eventId - The ID of the event
 * @returns The next waitlist position number
 */
async function getNextWaitlistPosition(eventId: string): Promise<number> {
  const { count, error } = await supabase
    .from('event_participants')
    .select('id', { count: 'exact', head: true })
    .eq('event_id', eventId)
    .eq('is_waitlisted', true)

  if (error) throw new Error(`Failed to count waitlist: ${error.message}`)
  return (count !== null ? count : 0) + 1
}

/**
 * Updates an existing event participant
 * @param participantId - The ID of the participant record
 * @param status - New attendance status
 * @param isWaitlisted - Whether the participant should be waitlisted
 * @param waitlistPosition - Position in the waitlist (if applicable)
 * @param notes - Optional RSVP notes
 * @returns Object containing the updated participant data or an error
 */
async function updateParticipant(
  participantId: string,
  status: AttendanceStatus,
  isWaitlisted: boolean,
  waitlistPosition: number | null,
  notes?: string
) {
  const { data, error } = await supabase
    .from('event_participants')
    .update({
      status,
      is_waitlisted: isWaitlisted,
      waitlist_position: waitlistPosition,
      rsvp_notes: notes,
    })
    .eq('id', participantId)
    .select()
    .single()

  if (error) throw new Error(`Failed to update event attendance: ${error.message}`)
  return { data, error: null }
}

/**
 * Creates a new event participant
 * @param eventId - The ID of the event
 * @param profileId - The ID of the user's profile
 * @param status - Attendance status
 * @param isWaitlisted - Whether the participant should be waitlisted
 * @param waitlistPosition - Position in the waitlist (if applicable)
 * @param notes - Optional RSVP notes
 * @returns Object containing the new participant data or an error
 */
async function createParticipant(
  eventId: string,
  profileId: string,
  status: AttendanceStatus,
  isWaitlisted: boolean,
  waitlistPosition: number | null,
  notes?: string
) {
  const { data, error } = await supabase
    .from('event_participants')
    .insert({
      event_id: eventId,
      profile_id: profileId,
      status,
      is_waitlisted: isWaitlisted,
      waitlist_position: waitlistPosition,
      rsvp_notes: notes,
    })
    .select()
    .single()

  if (error) throw new Error(`Failed to join event: ${error.message}`)
  return { data, error: null }
}

/**
 * Leaves an event (removes participant)
 * @param eventId - The ID of the event to leave
 * @param profileId - The profile ID of the user leaving
 * @returns Object indicating success or an error
 */
export async function leaveEvent(eventId: string, profileId: string) {
  try {
    console.log(`[leaveEvent] Starting with eventId: ${eventId}, profileId: ${profileId}`)

    // First check if the participant exists
    const { data: existingParticipant, error: checkError } = await supabase
      .from('event_participants')
      .select('id')
      .eq('event_id', eventId)
      .eq('profile_id', profileId)
      .maybeSingle()

    if (checkError) {
      console.error(`[leaveEvent] Error checking participant: ${checkError.message}`)
    }

    console.log(`[leaveEvent] Existing participant: ${JSON.stringify(existingParticipant)}`)

    if (!existingParticipant) {
      console.log(`[leaveEvent] No participant found to remove`)
      return { success: true, error: null }
    }

    const { error } = await supabase
      .from('event_participants')
      .delete()
      .match({ event_id: eventId, profile_id: profileId })

    if (error) {
      console.error(`[leaveEvent] Error during delete: ${error.message}`)
      throw new Error(`Failed to leave event: ${error.message}`)
    }

    console.log(`[leaveEvent] Successfully deleted participant, now reindexing waitlist`)

    // Re-index waitlist after someone leaves
    await reindexWaitlist(eventId)

    console.log(`[leaveEvent] Operation complete`)
    return { success: true, error: null }
  } catch (error) {
    console.error(`[leaveEvent] Error: ${error}`)
    return handleApiError(error, 'leaving the event')
  }
}

/**
 * Re-indexes waitlist positions after someone leaves
 * @param eventId - The ID of the event
 */
async function reindexWaitlist(eventId: string) {
  try {
    // Get all waitlisted participants in order
    const { data, error } = await supabase
      .from('event_participants')
      .select('id')
      .eq('event_id', eventId)
      .eq('is_waitlisted', true)
      .order('waitlist_position', { ascending: true })

    if (error) throw new Error(`Failed to get waitlist: ${error.message}`)
    if (!data || data.length === 0) return

    // Update each position to be sequential
    for (let i = 0; i < data.length; i++) {
      await supabase
        .from('event_participants')
        .update({ waitlist_position: i + 1 })
        .eq('id', data[i].id)
    }
  } catch (error) {
    console.error('Error reindexing waitlist:', error)
    // Non-critical operation, so just log the error
  }
}

/**
 * Gets all participants for an event with profile data
 * @param eventId - The ID of the event
 * @returns Object containing the list of participants or an error
 */
export async function getEventParticipants(eventId: string) {
  try {
    const { data, error } = await supabase
      .from('event_participants')
      .select(
        `
        id,
        created_at,
        event_id,
        profile_id,
        status,
        is_waitlisted,
        waitlist_position,
        rsvp_notes,
        profiles:profile_id (
          id,
          username,
          full_name,
          avatar_url
        )
      `
      )
      .eq('event_id', eventId)
      .order('is_waitlisted', { ascending: true })
      .order('waitlist_position', { ascending: true })
      .order('created_at', { ascending: true })

    if (error) throw new Error(`Failed to get event participants: ${error.message}`)

    return { data, error: null }
  } catch (error) {
    return handleApiError(error, 'fetching event participants')
  }
}

/**
 * Gets all participants for an event with their profile data merged (flattened)
 * @param eventId - The ID of the event
 * @returns Object containing the list of participants with merged profile data or an error
 */
export async function getEventParticipantsWithProfiles(eventId: string) {
  try {
    const { data: participants, error } = await supabase
      .from('event_participants')
      .select(
        `
        id,
        created_at,
        event_id,
        profile_id,
        status,
        is_waitlisted,
        waitlist_position,
        rsvp_notes,
        profiles:profile_id (
          id,
          username,
          full_name,
          avatar_url
        )
      `
      )
      .eq('event_id', eventId)
      .order('is_waitlisted', { ascending: true })
      .order('waitlist_position', { ascending: true })
      .order('created_at', { ascending: true })

    if (error) throw new Error(`Failed to get event participants: ${error.message}`)

    // Flatten the profile data
    const participantsWithProfiles = participants
      ? participants.map(participant => {
          // Extract profile information with type safety
          const profile = participant.profiles as unknown as {
            id: string
            username: string | null
            full_name: string | null
            avatar_url: string | null
          }

          return {
            id: participant.id,
            event_id: participant.event_id,
            profile_id: participant.profile_id,
            status: participant.status,
            is_waitlisted: participant.is_waitlisted,
            waitlist_position: participant.waitlist_position,
            rsvp_notes: participant.rsvp_notes,
            // Merge profile data directly into the participant object
            full_name: profile?.full_name || null,
            username: profile?.username || null,
            avatar_url: profile?.avatar_url || null,
          }
        })
      : []

    return { data: participantsWithProfiles, error: null }
  } catch (error) {
    return handleApiError(error, 'fetching event participants with profiles')
  }
}

/**
 * Gets attendance stats for an event
 * @param eventId - The ID of the event
 * @returns Object containing attendance statistics or an error
 */
export async function getEventAttendanceStats(eventId: string) {
  try {
    // Skip trying the RPC function since it doesn't exist (returns 404)
    // Use the direct query approach instead
    const { count, error } = await supabase
      .from('event_participants')
      .select('id', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .eq('status', 'im_in')
      .eq('is_waitlisted', false)

    if (error) throw new Error(`Failed to get confirmed count: ${error.message}`)

    // Use the count from the direct query
    return getRestOfAttendanceStats(eventId, count || 0)
  } catch (error) {
    return handleApiError(error, 'fetching attendance statistics')
  }
}

/**
 * Helper function to get the rest of the attendance stats
 * @param eventId - The ID of the event
 * @param confirmedCount - The count of confirmed attendees
 * @returns Object containing attendance statistics or an error
 */
async function getRestOfAttendanceStats(eventId: string, confirmedCount: number) {
  try {
    // Get maybe count directly using a query instead of RPC
    const { count: maybeCount, error: maybeError } = await supabase
      .from('event_participants')
      .select('id', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .eq('status', 'maybe')

    if (maybeError) throw new Error(`Failed to get maybe count: ${maybeError.message}`)

    // Get not_me count directly using a query instead of RPC
    const { count: notMeCount, error: notMeError } = await supabase
      .from('event_participants')
      .select('id', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .eq('status', 'not_me')

    if (notMeError) throw new Error(`Failed to get declined count: ${notMeError.message}`)

    // Get waitlist count directly using a query instead of RPC
    const { count: waitlistCount, error: waitlistError } = await supabase
      .from('event_participants')
      .select('id', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .eq('is_waitlisted', true)

    if (waitlistError) throw new Error(`Failed to get waitlist count: ${waitlistError.message}`)

    return {
      data: {
        imIn: confirmedCount,
        maybe: maybeCount || 0,
        notMe: notMeCount || 0,
        waitlist: waitlistCount || 0,
        total: confirmedCount + (maybeCount || 0) + (waitlistCount || 0),
      },
      error: null,
    }
  } catch (error) {
    return handleApiError(error, 'fetching attendance statistics')
  }
}
